// Landing page: i18n, waitlist form, country detection, social proof, UTM tracking
import { tzToCountry, langToCountry } from './landing-data.ts';
import { trackWaitlistSubscribe } from '../shared/analytics.ts';
import { api_v1_waitlist_path, api_v1_waitlist_count_path } from '../shared/api-routes';

// ── Country detection & language ───────────────────────────────────────
let currentLang = 'en'

const SUPPORTED = ['en','uk','pl','de','es','fr','pt','it','ar','tr','he','nl','el','hu','cs','ro','sk','sv','hr','bg']

// Auto-redirect to browser language on first visit (client-side only, safe for Google)
function autoRedirectOnFirstVisit() {
  // Only redirect from root / (not from /en, /uk etc.)
  if (window.location.pathname !== '/') return
  // Don't redirect if user already visited (has cookie)
  if (document.cookie.includes('locale_redirected=1')) return
  // Don't redirect bots
  if (/bot|crawl|spider|slurp|googlebot|bingbot|yandex/i.test(navigator.userAgent)) return

  // Detect browser language
  const browserLang = (navigator.language || '').split('-')[0].toLowerCase()

  // If browser language is supported and NOT English (since / is already English)
  if (browserLang && browserLang !== 'en' && SUPPORTED.includes(browserLang)) {
    // Set cookie to prevent redirect loop (expires in 30 days)
    document.cookie = 'locale_redirected=1; path=/; max-age=' + (30 * 24 * 60 * 60)
    window.location.href = '/' + browserLang
    return true
  }

  // Mark as redirected even if staying on English
  document.cookie = 'locale_redirected=1; path=/; max-age=' + (30 * 24 * 60 * 60)
  return false
}

// Detect language from the URL path (never localStorage)
function detectLanguage() {
  // 1. URL path: /en, /de, /fr, /fr/faq, /uk/blog/... — anchor on `/lang`
  //    followed by `/` or end-of-path so locale stays correct on every
  //    nested route, not only on the bare landing URL.
  const pathMatch = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)
  if (pathMatch && SUPPORTED.includes(pathMatch[1])) return pathMatch[1]

  return 'en'
}

/**
 * Client-side setup for the language the server already rendered.
 *
 * This used to re-render every `[data-i18n]` element from a translation table
 * compiled into the bundle (`landing-data.ts`, 20 language blocks, 137 KB).
 * The page is server-rendered from `config/locales/frontend/<lang>/landing.yml`
 * and the language comes from the URL, so that pass only ever overwrote
 * correct text with a second copy — and the copy had drifted: it still said
 * "165+ nozzle models" after the catalog counts were corrected to 202+ in the
 * YAML, so the fix was invisible in a browser in all 20 languages.
 *
 * `dir` and the email placeholder are set by the layout and the view
 * respectively, so nothing here needs to restate them.
 */
function setLanguage(lang: string) {
  currentLang = lang
  autoDetectCountry(lang)
}

// Detect country: timezone (85%) → browser language (70%) → fallback
function autoDetectCountry(lang: string) {
  const select = document.getElementById('country')
  if (!select) return

  // 1. Timezone (most accurate, free)
  let country = null
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    country = (tzToCountry as Record<string, string>)[tz]
  } catch { /* old browsers */ }

  // 2. Fallback: browser language
  if (!country) {
    const browserLang = navigator.language || ''
    country = (langToCountry as Record<string, string>)[browserLang] || (langToCountry as Record<string, string>)[lang]
  }

  if (country) {
    const sel = select as HTMLSelectElement;
    for (let i = 0; i < sel.options.length; i++) {
      if (sel.options[i].value === country) {
        sel.selectedIndex = i
        break
      }
    }
  }
}

// Fetch current waitlist count from API
function fetchWaitlistCount() {
  fetch(api_v1_waitlist_count_path())
    .then((r) => r.json())
    .then((data) => {
      const el = document.getElementById('waitlistCount')
      if (el) el.textContent = data.count || 0
    })
    .catch(() => {})
}

// Display a temporary error message below the form
function showFormError(msg: string) {
  const existing = document.querySelector('.form-error')
  if (existing) existing.remove()

  const div = document.createElement('div')
  div.className = 'form-error'
  div.textContent = msg
  document.querySelector('.waitlist-form')!.appendChild(div)

  setTimeout(() => div.remove(), 5000)
}

// Extract UTM source or referrer for attribution. The referrer counts by its
// host: the full URL (a search query, say) is more than attribution needs and
// longer than the 100 characters the waitlist contract accepts, which turned a
// long one into a 400 instead of a sign-up.
const SOURCE_MAX = 100

function getSource() {
  const params = new URLSearchParams(window.location.search)
  const source = params.get('utm_source') || params.get('ref') || referrerHost() || 'direct'
  return source.slice(0, SOURCE_MAX)
}

function referrerHost(): string {
  try {
    return document.referrer ? new URL(document.referrer).hostname : ''
  } catch {
    return ''
  }
}

// Initialize all landing page functionality
export function initLanding() {
  // Auto-redirect to browser language on first visit (Google bots are excluded)
  if (autoRedirectOnFirstVisit()) return

  const waitlistForm = document.getElementById('waitlistForm')
  if (waitlistForm) {
    waitlistForm.addEventListener('submit', (e) => {
      e.preventDefault()

      const emailInput = document.getElementById('email')
      const countrySelect = document.getElementById('country')
      const submitBtn = (e.target! as HTMLElement).querySelector('button[type="submit"]');
      const originalText = submitBtn!.textContent;

      (submitBtn as HTMLButtonElement).disabled = true;
      (submitBtn as HTMLButtonElement).textContent = '...'

      fetch(api_v1_waitlist_path(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: (emailInput as HTMLInputElement).value,
          country_code: (countrySelect as HTMLSelectElement).value,
          language: currentLang,
          source: getSource()
        })
      })
        .then((resp) => {
          if (resp.ok) return resp.json()
          return resp.json().then((err) => { throw err })
        })
        .then((data) => {
          trackWaitlistSubscribe(getSource())
          document.getElementById('formWrapper')!.hidden = true
          document.getElementById('thankYou')!.hidden = false
          const posEl = document.getElementById('positionNumber')
          if (posEl) posEl.textContent = data.position || '?'
          fetchWaitlistCount()
        })
        .catch((err) => {
          (submitBtn as HTMLButtonElement).disabled = false;
          (submitBtn as HTMLButtonElement).textContent = originalText

          const form = document.getElementById('waitlistForm')
          let msg = form?.dataset.errorGeneric
          if (err && err.error) {
            if (err.error.indexOf('already') !== -1) {
              msg = form?.dataset.errorDuplicate
            } else {
              msg = form?.dataset.errorInvalid
            }
          }
          // The three strings are rendered onto the form by the view; the
          // empty fallback keeps the type honest if a locale ever lacks one.
          showFormError(msg || '')
        })
    })
  }

  // Header scroll shadow (rAF-throttled to avoid layout thrash)
  let scrollTicking = false
  window.addEventListener('scroll', () => {
    if (scrollTicking) return
    scrollTicking = true
    requestAnimationFrame(() => {
      document.getElementById('header')?.classList.toggle('scrolled', window.scrollY > 10)
      scrollTicking = false
    })
  }, { passive: true })

  // Language always from URL, never from localStorage
  const lang = detectLanguage()
  currentLang = lang
  document.documentElement.lang = lang
  setLanguage(lang)
  fetchWaitlistCount()
}
